import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { startQrScanner, requestCameraPermission, classifyCameraError, CAMERA_ERROR_KEYS } from "@/lib/qrCamera";
import { useT } from "@/lib/i18n";
import { QrCode, Upload, Camera, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/** Generic QR → text scanner (camera or image) used for Ear Tag capture. Works offline. */
export default function TagScanDialog({ open, onOpenChange, onScan }) {
  const { t } = useT();
  const scannerRef = useRef(null);
  const [starting, setStarting] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const regionId = "am-tag-scan-region";

  const stop = async () => {
    try {
      if (scannerRef.current?.isScanning) await scannerRef.current.stop();
      scannerRef.current?.clear();
    } catch (e) { console.debug(e); }
    scannerRef.current = null;
  };

  const handleDecoded = async (text) => {
    await stop();
    onScan((text || "").trim());
    onOpenChange(false);
  };

  const start = async (attempt = 0) => {
    setCameraError(null);
    setStarting(true);
    try {
      scannerRef.current = await startQrScanner(regionId, handleDecoded, { qrbox: 220 });
    } catch (e) {
      const code = classifyCameraError(e);
      // Auto-retry once for transient failures.
      if (attempt === 0 && code !== "denied" && code !== "https" && code !== "nocamera") {
        setTimeout(() => start(1), 1200);
        return;
      }
      setCameraError(code);
    } finally { setStarting(false); }
  };

  const allowCamera = async () => {
    try {
      await requestCameraPermission();
      start();
    } catch (e) {
      setCameraError(classifyCameraError(e));
      toast.error(t("qr.errDenied"));
    }
  };

  // Effect keys on `open` only — a latest-callback ref keeps start/stop fresh
  // without re-running the camera lifecycle on unrelated re-renders.
  const lifecycleRef = useRef({ start, stop });
  lifecycleRef.current = { start, stop };
  useEffect(() => {
    const cam = lifecycleRef.current;
    if (!open) { cam.stop(); return; }
    const timer = setTimeout(() => cam.start(), 250);
    return () => { clearTimeout(timer); cam.stop(); };
  }, [open]);

  const scanFile = async (file) => {
    if (!file) return;
    try {
      await stop();
      const scanner = new Html5Qrcode(regionId);
      scannerRef.current = scanner;
      const text = await scanner.scanFile(file, false);
      await handleDecoded(text);
    } catch (e) {
      console.debug(e);
      toast.error("No QR code found in the image");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stop(); onOpenChange(o); }}>
      <DialogContent className="max-w-sm" data-testid="tag-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" /> Scan Ear Tag QR
          </DialogTitle>
          <DialogDescription>Point the camera at the tag QR code</DialogDescription>
        </DialogHeader>

        {cameraError && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-center space-y-3" data-testid="tag-camera-error">
            <ShieldAlert className="w-8 h-8 text-amber-700 mx-auto" />
            <p className="text-sm font-semibold text-amber-900" data-testid={`tag-camera-error-${cameraError}`}>
              {t(CAMERA_ERROR_KEYS[cameraError] || "qr.cameraBlocked")}
            </p>
            {cameraError === "denied" ? (
              <Button className="am-btn-primary w-full" onClick={allowCamera} data-testid="tag-allow-camera-btn">
                <Camera className="w-4 h-4 mr-2" /> {t("qr.allowCamera")}
              </Button>
            ) : cameraError !== "https" && cameraError !== "nocamera" ? (
              <Button className="am-btn-primary w-full" onClick={() => start()} data-testid="tag-retry-camera-btn">
                <Camera className="w-4 h-4 mr-2" /> {t("qr.retryCamera")}
              </Button>
            ) : null}
          </div>
        )}

        <div id={regionId} className="rounded-2xl overflow-hidden bg-black/90"
          style={{ minHeight: cameraError ? 0 : 240, display: cameraError ? "none" : "block" }} />
        {starting && !cameraError && <p className="text-xs text-muted-foreground text-center">Starting camera…</p>}

        <label className="w-full">
          <Button variant="outline" className="rounded-full w-full" asChild>
            <span><Upload className="w-4 h-4 mr-1.5" /> {t("qr.uploadImage")}</span>
          </Button>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => scanFile(e.target.files?.[0])} data-testid="tag-scan-file" />
        </label>
      </DialogContent>
    </Dialog>
  );
}
